import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  InputLabel,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import FavouritePrompt from "./FavouritePrompt.tsx";
import ExplainPrompt from "./ExplainPrompt.tsx";
import ThumbUpPrompt from "./ThumbUpPrompt.tsx";
import ThumbDownPrompt from "./ThumbDownPrompt.tsx";
import ErrorBoundary from "./ErrorBoundary.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import theme from "../../theme.tsx";

interface PromptPanelProps {
  children?: React.ReactNode;
  content: string;
  role: string;
  exchange_id?: string;
  feedback?: string | null;
  user_comment?: string | null;
  progress_update?: boolean | null;
}

const PromptPanel: React.FC<PromptPanelProps> = (props) => {
  const [favourite, setFavourite] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoggedIn = useTypedSelector((state) => state.authState.isLoggedIn);

  useEffect(() => {
    // Clean up the isHovering state and timeout when the component is about to be unmounted
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Box marginBottom={"4%"} marginLeft={"5%"}>
      <Grid
        container
        direction="row"
        spacing={2}
        alignContent="flex-start"
        alignItems="flex-start"
      >
        <Grid
          size={{ xs: 1, lg: 1 }}
          sx={{
            marginTop: "1%",
          }}
        >
          {/* Assistant's Avatar */}
          {props.role !== "user" && (
            <Grid
              size={{ xs: 1, lg: 1 }}
              sx={{
                marginLeft: { xs: "0%", md: "15%", lg: "5%", xl: "25%" },
                marginTop: { xs: "60%", md: "0%", lg: "5%", xl: "0%" },
              }}
            >
              <AvatarPanel />
            </Grid>
          )}
        </Grid>
        <Grid size={{ xs: 11, lg: 11 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: props.role === "user" ? "row-reverse" : "row",
              justifyContent:
                props.role === "user" ? "flex-start" : "flex-start",
            }}
          >
            <Box
              sx={{
                whiteSpace: "normal",
                backgroundColor:
                  props.role === "user" ? "white" : "transparent",
                color: props.role === "user" ? "black" : "inherit",
                borderRadius: props.role === "user" ? "15px" : "0",
                padding: props.role === "user" ? "0px 15px" : "0",
                maxWidth: props.role === "user" ? "100%" : "100%",
                display: "inline-block",
                marginLeft: props.role === "user" ? "20%" : "0%",
                marginRight:
                  props.role === "user"
                    ? useMediaQuery(theme.breakpoints.down("sm"))
                      ? "1%"
                      : "7%"
                    : "10%",
                marginTop: "0.8%",
              }}
            >
              <InputLabel
                sx={{
                  color: "black",
                  whiteSpace: "normal",
                }}
              >
                <ErrorBoundary>
                  <Typography
                    component="span"
                    sx={{
                      fontStyle: props.progress_update ? "italic" : "normal",
                      opacity: props.progress_update ? 0.6 : 1,
                      fontSize: {
                        xs: "0.8rem",
                        sm: "0.8rem",
                        md: "0.9rem",
                        lg: "1rem",
                        xl: "1rem",
                      },
                    }}
                  >
                    <CustomReactMarkDown
                      content={props.content}
                    ></CustomReactMarkDown>
                  </Typography>
                </ErrorBoundary>
              </InputLabel>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {props.role === "user" ? (
        <Grid
          position={"relative"}
          sx={{
            marginBottom: "7%",
            marginTop: "1%",
          }}
        >
          <Grid
            container
            sx={{
              display: "flex",
              direction: "row",
              justifyContent: "flex-end",
              marginBottom: "5%",
              paddingRight: {
                xs: "1%",
                sm: "6%",
                md: "6%",
                lg: "6%",
                xl: "6%",
              },
            }}
          >
            {isLoggedIn && (
              <>
                <Grid>
                  <FavouritePrompt
                    content={props.content}
                    clicked={favourite}
                    setClicked={setFavourite}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      ) : (
        props.role !== "user" && (
          <Grid
            position={"relative"}
            sx={{ marginLeft: "8%", marginBottom: "6%" }}
          >
            <Grid container direction="row" position={"absolute"}>
              {props.feedback !== "bad" && (
                <Grid>
                  <ThumbUpPrompt
                    exchange_id={props.exchange_id}
                    clicked={props.feedback === "good"}
                  />
                </Grid>
              )}
              {props.feedback !== "good" && (
                <Grid>
                  <ThumbDownPrompt
                    exchange_id={props.exchange_id}
                    clicked={props.feedback === "bad"}
                  />
                </Grid>
              )}
              <Grid>
                {isLoggedIn && (
                  <ExplainPrompt exchange_id={props.exchange_id} />
                )}
              </Grid>
            </Grid>
          </Grid>
        )
      )}
    </Box>
  );
};

export default PromptPanel;
